import { HttpErrorResponse } from '@angular/common/http';
import { GithubErrorCode, isGithubErrorCode } from '../../models/github-project-config.model';

/** Die Meldungen, die eine abgewiesene Pipeline-Aktion erzeugen kann (NEX-1236). */
export const PIPELINE_ERROR_KEYS = {
  tokenMissing: 'testRunner.live.pipeline.error.tokenMissing',
  tokenInvalid: 'testRunner.live.pipeline.error.tokenInvalid',
  repoNotAccessible: 'testRunner.live.pipeline.error.repoNotAccessible',
  rateLimited: 'testRunner.live.pipeline.error.rateLimited',
  workflowNotDispatchable: 'testRunner.live.pipeline.error.workflowNotDispatchable',
  notTriggerable: 'testRunner.live.pipeline.error.notTriggerable',
  notCancellable: 'testRunner.live.pipeline.error.notCancellable',
  alreadyFinished: 'testRunner.live.pipeline.error.alreadyFinished',
  unknown: 'testRunner.live.pipeline.error.unknown',
} as const;

/** Was von einer abgewiesenen Aktion anzuzeigen ist. */
export interface PipelineActionError {
  key: string;
  /**
   * Der Weg ins Konto steht nur da, wo im Konto etwas zu tun ist: das Token fehlt oder
   * es ist ungueltig und gehoert ersetzt. Bei jedem anderen Grund fuehrte er an eine
   * Stelle, an der nichts zu tun ist — ein rechtearmes Token etwa will beim Anbieter
   * freigegeben werden, nicht hier.
   */
  showsAccountLink: boolean;
}

/** Codes, die nur beim Starten und Abbrechen auftreten und daher nicht im Modell stehen. */
type PipelineActionCode = 'GITHUB_WORKFLOW_NOT_DISPATCHABLE' | 'PIPELINE_NOT_TRIGGERABLE' | 'PIPELINE_NOT_CANCELLABLE';

/** Die Gruende, gegen die im Konto etwas auszurichten ist. */
const FIXABLE_IN_ACCOUNT: readonly GithubErrorCode[] = ['GITHUB_TOKEN_MISSING', 'GITHUB_TOKEN_INVALID'];

const BY_CODE: Record<string, string> = {
  GITHUB_TOKEN_MISSING: PIPELINE_ERROR_KEYS.tokenMissing,
  GITHUB_TOKEN_INVALID: PIPELINE_ERROR_KEYS.tokenInvalid,
  GITHUB_REPO_NOT_ACCESSIBLE: PIPELINE_ERROR_KEYS.repoNotAccessible,
  GITHUB_RATE_LIMITED: PIPELINE_ERROR_KEYS.rateLimited,
  GITHUB_WORKFLOW_NOT_DISPATCHABLE: PIPELINE_ERROR_KEYS.workflowNotDispatchable,
  PIPELINE_NOT_TRIGGERABLE: PIPELINE_ERROR_KEYS.notTriggerable,
  PIPELINE_NOT_CANCELLABLE: PIPELINE_ERROR_KEYS.notCancellable,
} satisfies Partial<Record<GithubErrorCode | PipelineActionCode, string>>;

/**
 * Uebersetzt die Absage des Servers in eine Meldung, die sagt, was zu tun ist.
 *
 * <p>Als eigene Funktion und nicht in der Komponente: die Zuordnung ist Fachlogik und
 * soll ohne aufgebaute Oberflaeche pruefbar bleiben — dieselbe Linie wie
 * {@code RepositoryActionsService} fuer die Repository-Bindung.
 *
 * <p>Der Status geht dem {@code code} vor, wo er allein schon eindeutig ist: ein
 * {@code 409} bedeutet beim Abbruch immer, dass der Durchlauf bereits beendet war — ob
 * der Server das hier oder beim Anbieter gemerkt hat, aendert fuer die lesende Person
 * nichts.
 */
export function pipelineActionError(error: HttpErrorResponse): PipelineActionError {
  if (error.status === 409) {
    return { key: PIPELINE_ERROR_KEYS.alreadyFinished, showsAccountLink: false };
  }
  const code = (error.error as { code?: string } | null)?.code ?? '';
  return {
    key: BY_CODE[code] ?? PIPELINE_ERROR_KEYS.unknown,
    showsAccountLink: isGithubErrorCode(code) && FIXABLE_IN_ACCOUNT.includes(code),
  };
}
